import {
  dataDogError,
  dataDogIdentify,
  dataDogLogout,
  initDatadog,
  setDatadogContext,
} from "tools/data-dog";
import {
  initOpenReplay,
  openReplayAction,
  openReplayError,
  openReplayIdentify,
  openReplayLogout,
  setOpenReplayContext,
} from "tools/open-replay";
import firebase from "firebase/app";
import type { ObservabilityContext } from "ts/other";

/**
 * Fan-out facade over both observability providers — Datadog (Logs only;
 * RUM was cut for cost) and OpenReplay run in parallel. Each provider
 * function already no-ops on its own activation gate, so this file only
 * combines the calls — no duplicated activation logic.
 *
 * Call sites should import from here rather than tools/data-dog or
 * tools/open-replay directly, so call sites don't duplicate the pair of
 * calls and dropping a provider later is a one-file change.
 *
 * `openReplayProfile` and `openReplayReduxMiddleware` have no Datadog
 * equivalent and stay OpenReplay-only — call those directly.
 */
const safeCall = (label: string, fn: () => void): void => {
  try {
    fn();
  } catch (error) {
    // One provider throwing must never suppress the other's telemetry —
    // that would silently bias the A/B comparison this restore exists for.
    console.warn(`Observability provider failed (${label})`, error);
  }
};

export const initObservability = (): void => {
  safeCall("datadog:init", initDatadog);
  safeCall("openreplay:init", initOpenReplay);
};

export const observabilityIdentify = (user: firebase.User | null): void => {
  safeCall("datadog:identify", () => dataDogIdentify(user));
  safeCall("openreplay:identify", () => openReplayIdentify(user));
};

export const setObservabilityContext = (ctx: ObservabilityContext): void => {
  safeCall("datadog:context", () => setDatadogContext(ctx));
  safeCall("openreplay:context", () => setOpenReplayContext(ctx));
};

export const observabilityLogout = (): void => {
  safeCall("datadog:logout", dataDogLogout);
  safeCall("openreplay:logout", openReplayLogout);
};

export const observabilityAction = (
  name: string,
  context?: Record<string, unknown>
): void => {
  safeCall("openreplay:action", () => openReplayAction(name, context));
};

export const observabilityError = (
  error: unknown,
  context?: Record<string, unknown>
): void => {
  safeCall("datadog:error", () => dataDogError(error, context));
  safeCall("openreplay:error", () => openReplayError(error, context));
};
